/**
 * Client for the AI persona API.
 *
 * The one piece of shared understanding between this file and the Worker is the
 * error envelope: every failure comes back as `{"error": "<slug>", "message":
 * "..."}`. Branching on the slug rather than the prose is what lets the Worker
 * reword a message without breaking the UI.
 *
 * Transport rules that are easy to get wrong and are handled here:
 *
 * - A streaming request can still fail *before* the stream opens, in which case
 *   the response is ordinary JSON with a 4xx/5xx status. `response.ok` is
 *   therefore checked before the body is ever treated as a stream.
 * - SSE events do not align with network chunks. An event can be split across
 *   two reads and several events can arrive in one, so incoming text is buffered
 *   and only complete events (terminated by a blank line) are parsed.
 * - `X-Session-Id` is only readable because the Worker lists it in
 *   `Access-Control-Expose-Headers`. Cross-origin JavaScript cannot see response
 *   headers outside a small safelist by default.
 */

export type PersonaErrorCode =
  | "empty_body"
  | "invalid_json"
  | "invalid_payload"
  | "invalid_session_id"
  | "body_unreadable"
  | "not_found"
  | "method_not_allowed"
  | "message_too_large"
  | "payload_too_large"
  | "unsupported_media_type"
  | "rate_limited"
  | "internal_error"
  | "upstream_unavailable"
  | "upstream_invalid_response"
  | "storage_unavailable"
  | "service_misconfigured"
  /** Not from the API: the request never completed (offline, DNS, CORS). */
  | "network_error";

/** Failures worth offering a retry button for. The rest are the caller's fault. */
const RETRYABLE: ReadonlySet<PersonaErrorCode> = new Set([
  "rate_limited",
  "upstream_unavailable",
  "upstream_invalid_response",
  "storage_unavailable",
  "internal_error",
  "network_error",
]);

export class PersonaApiError extends Error {
  readonly code: PersonaErrorCode;
  readonly status: number;
  /** Seconds to wait, from the `Retry-After` header on a 429. */
  readonly retryAfterSeconds?: number;
  /** Extra fields the API attached, e.g. `limit` on a 413. */
  readonly detail?: Record<string, unknown>;

  constructor(
    code: PersonaErrorCode,
    message: string,
    status: number,
    options: { retryAfterSeconds?: number; detail?: Record<string, unknown> } = {},
  ) {
    super(message);
    this.name = "PersonaApiError";
    this.code = code;
    this.status = status;
    this.retryAfterSeconds = options.retryAfterSeconds;
    this.detail = options.detail;
  }

  get isRetryable(): boolean {
    return RETRYABLE.has(this.code);
  }
}

export interface ChatResult {
  reply: string;
  sessionId: string;
}

export interface HealthResult {
  status: string;
  model: string;
  max_tokens: number;
  persona: string;
  principal: string;
  bindings: { ai: boolean; conversation: boolean; rate_limiter: boolean };
  profile_complete: boolean;
  profile_gaps: string[];
}

export interface StreamOptions {
  signal?: AbortSignal;
  /** Called with each token as it arrives. */
  onToken?: (token: string, accumulated: string) => void;
}

/**
 * Minimal storage contract.
 *
 * `sessionStorage` is the default rather than `localStorage`: the session id is
 * the only thing guarding a transcript — there is no login behind it — so a
 * conversation should not outlive the tab that started it.
 */
export interface SessionStore {
  get(): string | null;
  set(id: string): void;
  clear(): void;
}

const SESSION_KEY = "persona.session_id";

function defaultSessionStore(): SessionStore {
  // Guarded so the client can be imported during SSR or in a test runner where
  // `window` does not exist. Falling back to memory keeps a conversation working
  // for the lifetime of the page instead of throwing on import.
  let memory: string | null = null;
  const available = (() => {
    try {
      return typeof window !== "undefined" && !!window.sessionStorage;
    } catch {
      // Some browsers throw on storage access when cookies are blocked.
      return false;
    }
  })();

  if (!available) {
    return {
      get: () => memory,
      set: (id) => {
        memory = id;
      },
      clear: () => {
        memory = null;
      },
    };
  }

  return {
    get: () => window.sessionStorage.getItem(SESSION_KEY),
    set: (id) => window.sessionStorage.setItem(SESSION_KEY, id),
    clear: () => window.sessionStorage.removeItem(SESSION_KEY),
  };
}

export interface PersonaClientOptions {
  /** Base URL of the Worker, without a trailing slash. */
  baseUrl: string;
  store?: SessionStore;
}

/**
 * Strip any trailing slash and insist on an absolute URL.
 *
 * A `baseUrl` with no scheme — `ask.irfansyafi.com` rather than
 * `https://ask.irfansyafi.com` — is not an error as far as `fetch` is
 * concerned. It is a *relative* URL, so the browser resolves it against the
 * page's own origin and quietly POSTs to
 * `https://<wherever the app is served>/ask.irfansyafi.com/api/chat`. That hits
 * the static host rather than the Worker and comes back as a 405, which reads
 * like a routing bug in the API and is nothing of the kind. Failing loudly at
 * construction costs one check and saves that entire debugging session.
 *
 * This is the last line of defence, not the first: it throws, and a throw from
 * inside a render blanks the page. UI should branch on `isAbsoluteHttpUrl`
 * *before* building a client, so a misconfigured deployment shows the setup
 * notice instead of a white screen. See `App.tsx`.
 */
export function isAbsoluteHttpUrl(value: string | undefined | null): value is string {
  return typeof value === "string" && /^https?:\/\//i.test(value.trim());
}

function normaliseBaseUrl(baseUrl: string): string {
  const trimmed = baseUrl.trim().replace(/\/+$/, "");
  if (!isAbsoluteHttpUrl(trimmed)) {
    throw new Error(
      `The persona API base URL must be absolute and include the scheme, ` +
        `e.g. "https://ai-api.irfansyafi.com" — received "${baseUrl}". ` +
        `Check VITE_PERSONA_API_URL; see frontend/.env.example.`,
    );
  }
  return trimmed;
}

export function createPersonaClient({ baseUrl, store }: PersonaClientOptions) {
  const root = normaliseBaseUrl(baseUrl);
  const session = store ?? defaultSessionStore();

  async function post(path: string, body: unknown, signal?: AbortSignal): Promise<Response> {
    let response: Response;
    try {
      response = await fetch(`${root}${path}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
        signal,
      });
    } catch (cause) {
      // An AbortError is the caller cancelling on purpose; it must not be
      // rewritten into a network failure the UI would show as an error.
      if (cause instanceof DOMException && cause.name === "AbortError") throw cause;
      throw new PersonaApiError(
        "network_error",
        "Could not reach the persona service.",
        0,
      );
    }

    if (!response.ok) throw await toApiError(response);
    return response;
  }

  async function toApiError(response: Response): Promise<PersonaApiError> {
    // The body may not be our envelope at all — an edge-level 502 or a 522 is
    // HTML. Anything unparseable degrades to a generic code for that status.
    let payload: Record<string, unknown> = {};
    try {
      payload = (await response.json()) as Record<string, unknown>;
    } catch {
      payload = {};
    }

    const code = (typeof payload.error === "string" ? payload.error : fallbackCode(response.status)) as PersonaErrorCode;
    const message =
      typeof payload.message === "string"
        ? payload.message
        : `Request failed with status ${response.status}.`;

    const { error: _error, message: _message, ...detail } = payload;
    const retryAfter = Number.parseInt(response.headers.get("Retry-After") ?? "", 10);

    return new PersonaApiError(code, message, response.status, {
      retryAfterSeconds: Number.isFinite(retryAfter) ? retryAfter : undefined,
      detail: Object.keys(detail).length > 0 ? detail : undefined,
    });
  }

  function fallbackCode(status: number): PersonaErrorCode {
    if (status === 429) return "rate_limited";
    if (status >= 500) return "upstream_unavailable";
    return "invalid_payload";
  }

  function rememberSession(id: string | null | undefined): string {
    if (id) session.set(id);
    return id ?? "";
  }

  return {
    getSessionId: () => session.get(),
    clearSessionId: () => session.clear(),

    /** Send a message and wait for the complete reply. */
    async send(message: string, options: { signal?: AbortSignal } = {}): Promise<ChatResult> {
      const response = await post(
        "/api/chat",
        { message, session_id: session.get() ?? undefined },
        options.signal,
      );
      const data = (await response.json()) as { reply: string; session_id: string };
      return { reply: data.reply, sessionId: rememberSession(data.session_id) };
    },

    /** Send a message and receive the reply token by token. */
    async stream(message: string, options: StreamOptions = {}): Promise<ChatResult> {
      const response = await post(
        "/api/chat?stream=1",
        { message, session_id: session.get() ?? undefined },
        options.signal,
      );

      const sessionId = rememberSession(response.headers.get("X-Session-Id"));

      if (!response.body) {
        throw new PersonaApiError(
          "upstream_invalid_response",
          "The persona returned an empty stream.",
          response.status,
        );
      }

      const reader = response.body.pipeThrough(new TextDecoderStream()).getReader();
      let buffer = "";
      let accumulated = "";

      try {
        for (;;) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += value;
          const events = buffer.split("\n\n");
          // The final element is whatever came after the last blank line: either
          // an empty string, or a partial event still waiting for the rest.
          buffer = events.pop() ?? "";

          for (const event of events) {
            const token = tokenFromEvent(event);
            if (token) {
              accumulated += token;
              options.onToken?.(token, accumulated);
            }
          }
        }
      } finally {
        // Releasing the lock lets the connection be torn down promptly when the
        // caller aborts mid-stream, instead of lingering until GC.
        reader.releaseLock();
      }

      // A stream cut short can leave a final complete event with no terminator.
      const trailing = tokenFromEvent(buffer);
      if (trailing) {
        accumulated += trailing;
        options.onToken?.(trailing, accumulated);
      }

      return { reply: accumulated.trim(), sessionId };
    },

    /** Erase the conversation held by the Worker, and forget it locally. */
    async reset(options: { signal?: AbortSignal } = {}): Promise<void> {
      const sessionId = session.get();
      if (!sessionId) return;
      try {
        await post("/api/session/reset", { session_id: sessionId }, options.signal);
      } finally {
        // Forget it locally even if the server call failed. The alternative is a
        // user who pressed "clear" and still sees the old thread continue.
        session.clear();
      }
    },

    async health(options: { signal?: AbortSignal } = {}): Promise<HealthResult> {
      const response = await fetch(`${root}/health`, { signal: options.signal });
      if (!response.ok) throw await toApiError(response);
      return (await response.json()) as HealthResult;
    },
  };
}

export type PersonaClient = ReturnType<typeof createPersonaClient>;

/**
 * Extract the token text from one SSE event block.
 *
 * Skips comment lines (`:` heartbeats) and the `[DONE]` sentinel, and tolerates
 * malformed JSON: one unparseable event should cost a few characters of the
 * reply, not the whole response.
 */
function tokenFromEvent(block: string): string {
  const data = block
    .split("\n")
    .filter((line) => line.startsWith("data:"))
    .map((line) => line.slice(5).trimStart())
    .join("\n");

  if (!data || data === "[DONE]") return "";

  try {
    const parsed = JSON.parse(data) as { response?: unknown };
    return typeof parsed.response === "string" ? parsed.response : "";
  } catch {
    return "";
  }
}
